import React, { useRef, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { TopBar } from './TopBar';
import { BottomBar } from './BottomBar';
import { NavRail } from './NavRail';
import { Users, WifiOff } from 'lucide-react';
import { TrialBanner } from './TrialBanner';
import { HardGate } from './HardGate';
import { useBean } from '../context/BeanContext';
import { useData } from '../context/DataContext';
import { NotificationCenter } from './NotificationCenter';
import { useLanguage } from '../functions/useLanguage';
import { useToast } from '../hooks/useToast';
import { Walkthrough } from './Walkthrough';
import { usePetPresence } from '../hooks/usePetPresence';

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { isOnline, justReconnected } = useOnlineStatus();
  const { pendingUploadCount, pendingWriteCount } = useData();
  const { showToast, ToastMount } = useToast();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const { profile: beanProfile } = useBean();
  const { otherActiveCarers } = usePetPresence();
  const [presenceDismissed, setPresenceDismissed] = useState(false);
  const pendingTotal = pendingUploadCount + pendingWriteCount;
  const prevPendingRef = useRef(0);

  // ── Reconnect toast ────────────────────────────────────────────────────────
  useEffect(() => {
    if (justReconnected && pendingTotal > 0) {
      showToast(t('layout.reconnectSyncing').replace('{n}', String(pendingTotal)), 'info');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justReconnected]);

  useEffect(() => {
    if (prevPendingRef.current > 0 && pendingTotal === 0 && isOnline) {
      showToast(t('layout.reconnectSynced'), 'success');
    }
    prevPendingRef.current = pendingTotal;
  }, [pendingTotal, isOnline, showToast, t]);

  // ── Walkthrough ────────────────────────────────────────────────────────────
  const [showWalkthrough, setShowWalkthrough] = useState(false);

  // Show on first load if the user is already available synchronously (e.g.
  // returning user with cached auth). Also re-evaluates whenever `user` changes
  // so that a brand-new account — where auth resolves after Layout mounts —
  // still triggers the walkthrough correctly.
  useEffect(() => {
    if (user && !localStorage.getItem('koda_walkthrough_shown')) {
      setShowWalkthrough(true);
    }
  }, [user]);

  useEffect(() => {
    const handler = () => {
      localStorage.removeItem('koda_walkthrough_shown');
      setShowWalkthrough(true);
    };
    window.addEventListener('koda:replay-walkthrough', handler);
    return () => window.removeEventListener('koda:replay-walkthrough', handler);
  }, []);

  // Restore scroll position when pathname changes
  useEffect(() => {
    const scrollPositions = JSON.parse(localStorage.getItem('scroll_positions') || '{}');
    const savedPosition = scrollPositions[location.pathname];
    
    if (mainRef.current && savedPosition !== undefined) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        if (mainRef.current) {
          mainRef.current.scrollTop = savedPosition;
        }
      }, 0);
    } else if (mainRef.current) {
      // Reset to top for new pages
      mainRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  // Save scroll position on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!mainRef.current) return;
      
      const scrollPositions = JSON.parse(localStorage.getItem('scroll_positions') || '{}');
      scrollPositions[location.pathname] = mainRef.current.scrollTop;
      localStorage.setItem('scroll_positions', JSON.stringify(scrollPositions));
    };

    const mainElement = mainRef.current;
    if (mainElement) {
      mainElement.addEventListener('scroll', handleScroll);
      return () => mainElement.removeEventListener('scroll', handleScroll);
    }
  }, [location.pathname]);

  // Reset presence banner dismiss when co-carers list changes
  useEffect(() => { if (otherActiveCarers.length > 0) setPresenceDismissed(false); }, [otherActiveCarers.length]);

  const isBeanPage = location.pathname === '/bean';
  const isGuidePage = location.pathname === '/guide' || location.pathname.startsWith('/library');

  if (!user) {
    return <div className="min-h-screen bg-white">{children}</div>;
  }

  const beanAccent = isBeanPage ? (beanProfile?.roomie_color ?? '#1976d2') : null;

  return (
    <HardGate>
    <ToastMount />
    <div
      className={cn("h-[100dvh] flex flex-col relative overflow-hidden")}
      style={beanAccent
        ? { background: `linear-gradient(160deg, ${beanAccent}1a 0%, ${beanAccent}08 100%)` }
        : { background: '#ffffff' }
      }
    >
      {/* Offline banner */}
      <NotificationCenter headless />

      {!isOnline && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2 text-amber-800 text-sm z-50">
          <WifiOff size={14} className="flex-shrink-0" />
          <span>{t('layout.offlineBanner')}</span>
        </div>
      )}
      {/* Co-carer presence banner */}
      {otherActiveCarers.length > 0 && !presenceDismissed && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center justify-between gap-2 text-blue-800 text-sm z-50">
          <div className="flex items-center gap-2">
            <Users size={14} className="flex-shrink-0" />
            <span>
              {otherActiveCarers.map(c => c.display_name).join(', ')}
              {otherActiveCarers.length === 1 ? ' is' : ' are'} also viewing this pet right now
            </span>
          </div>
          <button onClick={() => setPresenceDismissed(true)} className="text-blue-500 hover:text-blue-700 font-bold px-1">×</button>
        </div>
      )}
      {/* Trial warning banner */}
      {!isBeanPage && <TrialBanner />}

      {/* Top Bar — full width, sticky, desktop only */}
      {!isBeanPage && <div className="hidden lg:block"><TopBar userName={user.full_name} walkthroughActive={showWalkthrough} /></div>}

      {/* Main Layout Container */}
      <div className="flex flex-1 min-h-0">
        {/* Nav Rail (fixed on desktop) */}
        <NavRail />

        {/* Main Content */}
        <main
          ref={mainRef}
          id="main-scroll"
          className={cn(
            'flex-1 min-w-0 overflow-y-auto transition-all duration-300',
            isBeanPage ? 'h-screen' : 'h-full'
          )}
        >
          {/* Top Bar — inside scroll on mobile only (scrolls away) */}
          {!isBeanPage && <div className="lg:hidden"><TopBar userName={user.full_name} walkthroughActive={showWalkthrough} /></div>}
          <div
            className={cn(
              'w-full max-w-[1280px] mx-auto flex flex-col items-center min-h-full',
              isBeanPage
                ? 'p-0'
                : isGuidePage
                ? 'pt-0 px-0 lg:pb-4 lg:px-4 lg:pt-3'
                : 'pt-4 px-4 lg:pb-4 lg:px-4 lg:pt-4'
            )}
          >
            {children}
          </div>
        </main>
      </div>

      {/* Bottom Bar (mobile) */}
      <BottomBar walkthroughActive={showWalkthrough} />

      {/* First-launch walkthrough */}
      {showWalkthrough && <Walkthrough onDismiss={() => setShowWalkthrough(false)} />}
    </div>
    </HardGate>
  );
}
